import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Input, Card, Statistic, Button, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark, faTriangleExclamation, faRotate, faShieldHalved, faCoins, faBan, faSkullCrossbones, faCircleQuestion, faShieldVirus } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { SERVER_URL } from '../../../databucket';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const EmailVerificationHistory = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${SERVER_URL}/api/ses/verification-history`, {
        params: { page, pageSize: 50, search: search || undefined },
      });
      setData(res.rows || []);
      setTotal(res.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search]);

  const fetchStats = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${SERVER_URL}/api/ses/verification-stats`);
      setStats(res);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Lead ID',
      dataIndex: 'leadDisplayId',
      key: 'leadDisplayId',
      width: 80,
      render: (text) => text ? <span style={{ fontWeight: 600, color: '#3b82f6' }}>{text}</span> : '—',
    },
    {
      title: 'Lead',
      dataIndex: 'leadName',
      key: 'leadName',
      width: 160,
      render: (text) => text || '—',
    },
    {
      title: 'Contact',
      dataIndex: 'contactName',
      key: 'contactName',
      width: 140,
      render: (text) => text || '—',
    },
    {
      title: 'Status',
      dataIndex: 'code',
      key: 'status',
      width: 130,
      render: (code, record) => {
        const c = (code || '').toLowerCase();
        const styles = {
          valid:        { color: '#16a34a', bg: '#f0fdf4', border: '#86efac', icon: faCircleCheck,          label: 'valid' },
          'catch-all':  { color: '#d97706', bg: '#fffbeb', border: '#fcd34d', icon: faTriangleExclamation,  label: 'catch-all' },
          catch_all:    { color: '#d97706', bg: '#fffbeb', border: '#fcd34d', icon: faTriangleExclamation,  label: 'catch-all' },
          do_not_mail:  { color: '#ea580c', bg: '#fff7ed', border: '#fdba74', icon: faBan,                  label: 'do_not_mail' },
          invalid:      { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', icon: faCircleXmark,         label: 'invalid' },
          spamtrap:     { color: '#991b1b', bg: '#fef2f2', border: '#f87171', icon: faSkullCrossbones,      label: 'spamtrap' },
          abuse:        { color: '#991b1b', bg: '#fef2f2', border: '#f87171', icon: faShieldVirus,          label: 'abuse' },
          unknown:      { color: '#64748b', bg: '#f8fafc', border: '#cbd5e1', icon: faCircleQuestion,       label: 'unknown' },
        };
        const s = record.isValid ? styles.valid : (styles[c] || styles.invalid);
        return (
          <Tag style={{ borderRadius: 20, fontWeight: 700, fontSize: 11, color: s.color, background: s.bg, border: `1.5px solid ${s.border}`, padding: '1px 10px' }}>
            <FontAwesomeIcon icon={s.icon} style={{ marginRight: 4 }} />
            {s.label}
          </Tag>
        );
      },
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      width: 180,
      render: (text) => <span style={{ color: '#64748b', fontSize: 12 }}>{text || '—'}</span>,
    },
    {
      title: 'Verified By',
      dataIndex: 'verifiedByName',
      key: 'verifiedByName',
      width: 140,
      render: (text) => text || '—',
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 80,
      render: (text) => (
        <Tag color={text === 'lead' ? 'blue' : text === 're-verify' ? 'orange' : 'default'} style={{ borderRadius: 10, fontSize: 10 }}>
          {text || 'lead'}
        </Tag>
      ),
    },
    {
      title: 'Verified At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (text) => text ? (
        <span style={{ fontSize: 12, color: '#64748b' }}>{dayjs(text).format('DD MMM YYYY, hh:mm A')}</span>
      ) : '—',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: 'descend',
    },
  ];

  const creditsColor = stats.credits > 1000 ? '#16a34a' : stats.credits > 500 ? '#d97706' : '#dc2626';

  return (
    <div style={{ padding: '20px 24px' }}>
      <h2 style={{ marginBottom: 16, fontWeight: 700, fontSize: 20 }}>
        <FontAwesomeIcon icon={faShieldHalved} style={{ marginRight: 8, color: '#3b82f6' }} />
        Email Verification History
      </h2>

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #e2e8f0', flex: '1 1 0', minWidth: 110 }}>
          <Statistic title="Total" value={stats.total || 0} valueStyle={{ color: '#1e293b', fontWeight: 700 }} />
        </Card>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #86efac', flex: '1 1 0', minWidth: 110 }}>
          <Statistic title="Valid" value={stats.valid || 0} valueStyle={{ color: '#16a34a', fontWeight: 700 }} prefix={<FontAwesomeIcon icon={faCircleCheck} />} />
        </Card>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #fca5a5', flex: '1 1 0', minWidth: 110 }}>
          <Statistic title="Invalid" value={stats.invalid || 0} valueStyle={{ color: '#dc2626', fontWeight: 700 }} prefix={<FontAwesomeIcon icon={faCircleXmark} />} />
        </Card>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #fcd34d', flex: '1 1 0', minWidth: 110 }}>
          <Statistic title="Catch-all" value={stats.catchAll || 0} valueStyle={{ color: '#d97706', fontWeight: 700 }} prefix={<FontAwesomeIcon icon={faTriangleExclamation} />} />
        </Card>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #fdba74', flex: '1 1 0', minWidth: 110 }}>
          <Statistic title="Do Not Mail" value={stats.doNotMail || 0} valueStyle={{ color: '#ea580c', fontWeight: 700 }} prefix={<FontAwesomeIcon icon={faBan} />} />
        </Card>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #f87171', flex: '1 1 0', minWidth: 110 }}>
          <Statistic title="Spamtrap" value={stats.spamtrap || 0} valueStyle={{ color: '#991b1b', fontWeight: 700 }} prefix={<FontAwesomeIcon icon={faSkullCrossbones} />} />
        </Card>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #f87171', flex: '1 1 0', minWidth: 110 }}>
          <Statistic title="Abuse" value={stats.abuse || 0} valueStyle={{ color: '#991b1b', fontWeight: 700 }} prefix={<FontAwesomeIcon icon={faShieldVirus} />} />
        </Card>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #cbd5e1', flex: '1 1 0', minWidth: 110 }}>
          <Statistic title="Unknown" value={stats.unknown || 0} valueStyle={{ color: '#64748b', fontWeight: 700 }} prefix={<FontAwesomeIcon icon={faCircleQuestion} />} />
        </Card>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #93c5fd', flex: '1 1 0', minWidth: 110 }}>
          <Statistic title="Today" value={stats.todayCount || 0} valueStyle={{ color: '#3b82f6', fontWeight: 700 }} />
        </Card>
        <Card size="small" style={{ borderRadius: 12, border: `1.5px solid ${creditsColor}33`, flex: '1 1 0', minWidth: 130 }}>
          <Statistic
            title="ZeroBounce Credits"
            value={stats.credits ?? '—'}
            valueStyle={{ color: creditsColor, fontWeight: 700 }}
            prefix={<FontAwesomeIcon icon={faCoins} />}
          />
          {stats.credits !== null && stats.credits < 500 && (
            <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2 }}>Low credits!</div>
          )}
        </Card>
      </div>

      {/* Search + Refresh */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <Input.Search
          placeholder="Search by email, lead, contact, or user..."
          allowClear
          onSearch={handleSearch}
          style={{ maxWidth: 400 }}
        />
        <Button icon={<FontAwesomeIcon icon={faRotate} />} onClick={() => { fetchHistory(); fetchStats(); }}>
          Refresh
        </Button>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={data}
        rowKey={(r) => r._id}
        loading={loading}
        pagination={{
          current: page,
          total,
          pageSize: 50,
          onChange: (p) => setPage(p),
          showTotal: (t) => `${t} verifications`,
          size: 'small',
        }}
        size="small"
        scroll={{ x: 1200 }}
        style={{ borderRadius: 12, overflow: 'hidden' }}
      />
    </div>
  );
};

export default EmailVerificationHistory;
